import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, GitMerge, User, Users, AlertTriangle, CheckCircle } from 'lucide-react';
import { concessionsMap } from '../../data/toolsData';

const ConcessionsTool = () => {
  const navigate = useNavigate();
  const [partner1Choices, setPartner1Choices] = useState({});
  const [partner2Choices, setPartner2Choices] = useState({});
  const [activePartner, setActivePartner] = useState(1);
  const [showResults, setShowResults] = useState(false);

  const handleChoice = (aspectId, value) => {
    if (activePartner === 1) {
      setPartner1Choices(prev => ({ ...prev, [aspectId]: value }));
    } else {
      setPartner2Choices(prev => ({ ...prev, [aspectId]: value }));
    }
  };

  const getComparison = (aspectId) => {
    const p1 = partner1Choices[aspectId];
    const p2 = partner2Choices[aspectId];
    
    if (!p1 || !p2) return null;
    
    const diff = Math.abs(p1 - p2);
    if (diff === 0) return { type: 'aligned', label: 'Alinhados' };
    if (diff === 1) return { type: 'close', label: 'Próximos' };
    if (diff === 2) return { type: 'attention', label: 'Conversar' };
    return { type: 'conflict', label: 'Divergente' };
  };

  const allAspectsAnswered = () => {
    const allAspects = concessionsMap.categories.flatMap(c => c.aspects.map(a => a.id));
    return allAspects.every(id => partner1Choices[id] && partner2Choices[id]);
  };

  const getOverallAnalysis = () => {
    const comparisons = concessionsMap.categories.flatMap(c => 
      c.aspects.map(a => getComparison(a.id))
    ).filter(Boolean);

    const aligned = comparisons.filter(c => c.type === 'aligned').length;
    const close = comparisons.filter(c => c.type === 'close').length;
    const attention = comparisons.filter(c => c.type === 'attention').length;
    const conflict = comparisons.filter(c => c.type === 'conflict').length;

    return { aligned, close, attention, conflict, total: comparisons.length };
  };

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/ferramentas')}
          className="flex items-center gap-2 text-[#8B7D6B] hover:text-[#6B4E37] mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          Voltar às ferramentas
        </button>

        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl bg-[#D4C4B0]/30 flex items-center justify-center mx-auto mb-6">
            <GitMerge size={32} className="text-[#6B4E37]" />
          </div>
          <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl text-[#2C2C2C] mb-4">
            {concessionsMap.title}
          </h1>
          <p className="text-lg text-[#6B4E37] max-w-xl mx-auto">
            {concessionsMap.subtitle}
          </p>
        </div>

        {/* Partner Selector */}
        {!showResults && (
          <div className="flex justify-center gap-4 mb-8">
            <button
              onClick={() => setActivePartner(1)}
              className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 ${
                activePartner === 1
                  ? 'bg-[#C4B5A0] text-white shadow-md'
                  : 'bg-white/60 text-[#6B4E37] hover:bg-white/80'
              }`}
            >
              <User size={18} />
              Parceiro 1
            </button>
            <button
              onClick={() => setActivePartner(2)}
              className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 ${
                activePartner === 2
                  ? 'bg-[#C4B5A0] text-white shadow-md'
                  : 'bg-white/60 text-[#6B4E37] hover:bg-white/80'
              }`}
            >
              <User size={18} />
              Parceiro 2
            </button>
          </div>
        )}

        {/* Importance Scale Legend */}
        {!showResults && (
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {concessionsMap.importanceLevels.map((level) => (
              <div 
                key={level.value}
                className="flex items-center gap-2 px-3 py-1 rounded-full text-xs"
                style={{ backgroundColor: `${level.color}30` }}
              >
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: level.color }}
                />
                <span className="text-[#6B4E37]">{level.label}</span>
              </div>
            ))}
          </div>
        )}

        {/* Categories and Aspects */}
        {!showResults && (
          <div className="space-y-8">
            {concessionsMap.categories.map((category) => (
              <div key={category.id} className="bg-white/60 backdrop-blur-sm rounded-2xl p-6">
                <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#2C2C2C] mb-6">
                  {category.name}
                </h3>
                
                <div className="space-y-4">
                  {category.aspects.map((aspect) => {
                    const currentChoice = activePartner === 1 
                      ? partner1Choices[aspect.id] 
                      : partner2Choices[aspect.id];
                    
                    return (
                      <div 
                        key={aspect.id}
                        className="bg-white/80 rounded-xl p-4"
                      >
                        <p className="text-[#2C2C2C] mb-3">{aspect.name}</p>
                        
                        <div className="flex gap-2">
                          {concessionsMap.importanceLevels.map((level) => (
                            <button
                              key={level.value}
                              onClick={() => handleChoice(aspect.id, level.value)}
                              className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all duration-300 ${
                                currentChoice === level.value
                                  ? 'text-white shadow-md'
                                  : 'bg-[#F5F3F0] text-[#8B7D6B] hover:opacity-80'
                              }`}
                              style={{ 
                                backgroundColor: currentChoice === level.value ? level.color : undefined
                              }}
                            >
                              {level.value}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Progress & Compare Button */}
        {!showResults && (
          <div className="mt-8 text-center">
            <p className="text-sm text-[#8B7D6B] mb-4">
              Parceiro 1: {Object.keys(partner1Choices).length}/16 • 
              Parceiro 2: {Object.keys(partner2Choices).length}/16
            </p>
            {allAspectsAnswered() && (
              <button
                onClick={() => setShowResults(true)}
                className="px-8 py-3 bg-[#C4B5A0] text-white rounded-lg font-medium hover:bg-[#6B4E37] transition-colors shadow-md"
              >
                <Users className="inline mr-2" size={18} />
                Comparar Respostas
              </button>
            )}
          </div>
        )}

        {/* Results View */}
        {showResults && (
          <div className="animate-fade-in">
            {/* Overall Analysis */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 mb-8 text-center">
              <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#2C2C2C] mb-6">
                Análise do Casal
              </h3>
              <div className="flex justify-center gap-6 mb-6">
                <div className="text-center">
                  <div className="text-3xl font-light text-[#B5C4B1]">{getOverallAnalysis().aligned}</div>
                  <div className="text-xs text-[#8B7D6B]">Alinhados</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-light text-[#D4C4B0]">{getOverallAnalysis().close}</div>
                  <div className="text-xs text-[#8B7D6B]">Próximos</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-light text-[#E8D5D5]">{getOverallAnalysis().attention}</div>
                  <div className="text-xs text-[#8B7D6B]">Conversar</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-light text-[#C5D1DC]">{getOverallAnalysis().conflict}</div>
                  <div className="text-xs text-[#8B7D6B]">Divergente</div>
                </div>
              </div>
            </div>

            {/* Detailed Comparison */}
            {concessionsMap.categories.map((category) => (
              <div key={category.id} className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 mb-6">
                <h3 className="font-['Cormorant_Garamond'] text-xl text-[#2C2C2C] mb-4">
                  {category.name}
                </h3>
                
                <div className="space-y-3">
                  {category.aspects.map((aspect) => {
                    const comparison = getComparison(aspect.id);
                    const p1 = partner1Choices[aspect.id];
                    const p2 = partner2Choices[aspect.id];
                    
                    return (
                      <div 
                        key={aspect.id}
                        className={`bg-white/80 rounded-lg p-4 flex items-center justify-between ${
                          comparison?.type === 'conflict' ? 'border-l-4 border-[#E8D5D5]' :
                          comparison?.type === 'attention' ? 'border-l-4 border-[#D4C4B0]' : ''
                        }`}
                      >
                        <span className="text-[#2C2C2C] text-sm flex-1">{aspect.name}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-xs px-2 py-1 bg-[#F5F3F0] rounded">P1: {p1}</span>
                          <span className="text-xs px-2 py-1 bg-[#F5F3F0] rounded">P2: {p2}</span>
                          {comparison && (
                            <span className={`text-xs px-3 py-1 rounded-full ${
                              comparison.type === 'aligned' ? 'bg-[#B5C4B1]/30 text-[#6B4E37]' :
                              comparison.type === 'close' ? 'bg-[#D4C4B0]/30 text-[#6B4E37]' :
                              comparison.type === 'attention' ? 'bg-[#E8D5D5]/30 text-[#6B4E37]' :
                              'bg-[#C5D1DC]/30 text-[#6B4E37]'
                            }`}>
                              {comparison.label}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="text-center">
              <button
                onClick={() => setShowResults(false)}
                className="text-[#8B7D6B] hover:text-[#6B4E37] underline"
              >
                Editar respostas
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConcessionsTool;
